/**
 * The rulebook as a graph, for the rule panel (turn `3c`).
 *
 * The panel promises three things the old modal did not: which screens a rule governs, which
 * rules relate to it, and how it has been amended. All three are derivable from `rulebook.json`
 * — it already carries `supersedes`, `supersededBy` and `topics` — so none of them is a list
 * anybody has to maintain by hand and none can drift from the rulebook itself.
 *
 * The one thing that is a hand-written map is topic → screen, because that is a fact about this
 * app, not about the league: the rules do not know that keeper costs are shown on a page called
 * The Grid.
 */

import rulebook from '../data/rulebook.json';

export interface RulebookEntry {
  id: string;
  year: number;
  section: string;
  title: string;
  status: string;
  summary: string;
  text: string;
  topics?: string[];
  supersedes?: string[];
  supersededBy?: string[];
  outcome?: string;
  /**
   * In force today, but only until the Competition Committee re-votes it. Eleven rules carry it,
   * including the two 2025 proposals that passed conditionally — the farm draft's second round and
   * the keeper apron — which lapse in 2026 without a super-majority.
   *
   * It has been in `rulebook.json` since the file was generated and was simply not declared here,
   * so every consumer of this module has been blind to it. Proposals phase 1 lists exactly these
   * rules as automatic docket items, which is what surfaced the omission.
   */
  provisional?: boolean;
}

/** Every rule, in file order. Exported for the group panes, which query rather than look up. */
export const RULES = (rulebook as { rules: RulebookEntry[] }).rules;
const BY_ID = new Map(RULES.map((r) => [r.id, r]));

export const ruleEntry = (id: string): RulebookEntry | undefined => BY_ID.get(id);

/* -- which screens a rule governs -------------------------------------------------------- */

export interface GovernedScreen {
  section: string;
  page: string;
  /** How the nav reads it: the breadcrumb a manager would follow. */
  label: string;
  /** The thing you would go there to DO, for the panel's primary button. */
  action: string;
}

/**
 * Topic → the screens that enforce it.
 *
 * Deliberately not one entry per rule. A rule about keeper penalties governs the same screens
 * as every other rule about keeper penalties, and a per-rule table would be 141 rows that go
 * stale the first time a page is renamed.
 */
const SCREENS_BY_TOPIC: Readonly<Record<string, GovernedScreen[]>> = {
  keepers: [
    { section: 'keepers', page: 'slate', label: 'Keepers › My slate', action: 'Declare keepers' },
    { section: 'league', page: 'grid', label: 'League › The Grid', action: 'Open the Grid' },
  ],
  draft: [
    { section: 'draft', page: 'order', label: 'Draft › Order and lottery', action: 'Rank slots' },
    { section: 'draft', page: 'order', label: 'Draft › Order & lottery', action: 'Open the lottery' },
  ],
  'farm-system': [
    { section: 'keepers', page: 'farm', label: 'Keepers › Farm', action: 'Open the farm' },
  ],
  faab: [
    { section: 'league', page: 'faab', label: 'League › FAAB', action: 'Open the ledger' },
  ],
  trades: [
    { section: 'league', page: 'trades', label: 'League › Trades', action: 'Open trades' },
  ],
  governance: [
    { section: 'league', page: 'proposals', label: 'League › Proposals', action: 'Open proposals' },
  ],
  commissioner: [
    { section: 'settings', page: 'deadlines', label: 'Settings › Deadlines', action: 'Open settings' },
  ],
  scoring: [
    { section: 'league', page: 'scoring', label: 'League › Scoring', action: 'Open scoring' },
  ],
  roster: [
    { section: 'league', page: 'scoring', label: 'League › Scoring', action: 'Open scoring' },
  ],
  wagering: [
    { section: 'league', page: 'faab', label: 'League › FAAB', action: 'Open the ledger' },
  ],
};

export function screensFor(id: string): GovernedScreen[] {
  const rule = BY_ID.get(id);
  if (!rule) return [];
  const out: GovernedScreen[] = [];
  const seen = new Set<string>();
  for (const topic of rule.topics ?? []) {
    for (const screen of SCREENS_BY_TOPIC[topic] ?? []) {
      const key = `${screen.section}/${screen.page}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(screen);
    }
  }
  return out;
}

/* -- how a rule got here, and what happened to it ----------------------------------------- */

export interface Amendment {
  id: string;
  year: number;
  title: string;
  /** `adopted` for the rule itself, `replaced` for what it superseded, `superseded` for after. */
  kind: 'adopted' | 'replaced' | 'superseded';
  outcome?: string;
}

/**
 * The chain a rule sits in, oldest first.
 *
 * Walks `supersedes` backwards and `supersededBy` forwards from the rule in hand, so the panel
 * can state "adopted 2019, amended 2020" without anybody writing that sentence down. Cycles are
 * possible in hand-maintained data and would hang the render, so every id is visited once.
 */
export function amendmentChain(id: string): Amendment[] {
  const rule = BY_ID.get(id);
  if (!rule) return [];

  const seen = new Set<string>([id]);
  const older: Amendment[] = [];
  const walkBack = (from: RulebookEntry) => {
    for (const prev of from.supersedes ?? []) {
      if (seen.has(prev)) continue;
      seen.add(prev);
      const r = BY_ID.get(prev);
      if (!r) continue;
      walkBack(r);
      older.push({ id: r.id, year: r.year, title: r.title, kind: 'replaced', outcome: r.outcome });
    }
  };
  walkBack(rule);

  const newer: Amendment[] = [];
  const walkOn = (from: RulebookEntry) => {
    for (const next of from.supersededBy ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      const r = BY_ID.get(next);
      if (!r) continue;
      newer.push({ id: r.id, year: r.year, title: r.title, kind: 'superseded', outcome: r.outcome });
      walkOn(r);
    }
  };
  walkOn(rule);

  return [
    ...older,
    { id: rule.id, year: rule.year, title: rule.title, kind: 'adopted', outcome: rule.outcome },
    ...newer,
  ];
}

/**
 * Rules on the same subject, nearest year first — the panel's "Related" chips.
 *
 * Anything already in the amendment chain is excluded: a rule that replaced this one is not
 * "related to" it, it is the next line of its own history, and showing it twice makes the panel
 * look like it does not know the difference.
 */
export function relatedTo(id: string, limit = 4): RulebookEntry[] {
  const rule = BY_ID.get(id);
  if (!rule?.topics?.length) return [];
  const inChain = new Set(amendmentChain(id).map((a) => a.id));
  const topics = new Set(rule.topics);
  return RULES
    .filter((r) => !inChain.has(r.id)
      && r.status !== 'failed-proposal'
      && (r.topics ?? []).some((t) => topics.has(t)))
    .sort((a, b) => Math.abs(a.year - rule.year) - Math.abs(b.year - rule.year)
      || a.id.localeCompare(b.id))
    .slice(0, limit);
}

/* -- anchors: the destinations a `§` needs and the panel cannot reach yet -------------------- */

/**
 * A citation is not always a rule.
 *
 * `<Rule id="2018-II-5">` works because the destination is a whole rule and the panel knows how to
 * open one. Three of the four `§` links the draft-order screen carries are not that shape:
 *
 *   - "how the lottery works" is TWO rules, 2014-II-1 and 2020-II-1, and neither alone is the
 *     answer — one creates the draw, the other changed what winning it gets you;
 *   - "the odds table" is one SECTION, 2014-II-1 §1, inside a rule whose other four sections are
 *     about something else;
 *   - "the Toilet Bowl priority" is a superseded rule (2015-II-4) read together with the section
 *     that replaced it (2020-II-1 §3), which is the only way the answer makes sense.
 *
 * `RuleText` can open a rule and it can open a hand-written INDEX of rules, but it cannot open a
 * section, and an index whose title is the question is not the same as an answer. So the four are
 * declared here, by subject rather than by screen — Farm and the Calculator want the same registry
 * — and left unresolved.
 *
 * TODO(rulebook-anchors): teach the panel to open one of these — deep-link a section, and render a
 * multi-rule anchor as a titled reading rather than a list — then flip `ANCHORS_LIVE`. Until that
 * happens `anchorTarget` returns null and every `§` wired to one is deliberately inert.
 * `draft-order-brief.md` §7; `calculator-brief.md` §8 is the same blocker.
 */
export interface RuleAnchor {
  id: string;
  /** What the reader is being sent to find out, in his words. Also the `§`'s accessible name. */
  label: string;
  /** Where the answer is. `section` names the section when only part of the rule is meant. */
  rules: ReadonlyArray<{ id: string; section?: number }>;
}

export const RULE_ANCHORS: Readonly<Record<string, RuleAnchor>> = {
  'lottery-how-it-works': {
    id: 'lottery-how-it-works',
    label: 'How the lottery works',
    rules: [{ id: '2014-II-1' }, { id: '2020-II-1' }],
  },
  'lottery-odds': {
    id: 'lottery-odds',
    label: 'The lottery odds',
    rules: [{ id: '2014-II-1', section: 1 }],
  },
  'toilet-bowl-priority': {
    id: 'toilet-bowl-priority',
    label: 'The Toilet Bowl winner’s priority',
    rules: [{ id: '2015-II-4' }, { id: '2020-II-1', section: 3 }],
  },
  'third-place-swap': {
    id: 'third-place-swap',
    label: 'The third-place pick swap',
    rules: [{ id: '2018-II-5' }],
  },

  /*
   * ONE REGISTRY, NOT TWO. These four lived in `domain/rulebookAnchors.ts` under a second type,
   * with a second liveness flag and a second inert-mark component — a duplicate that was flagged
   * when the Calculator shipped and deferred because neither half could resolve anything anyway.
   * The rulebook page ends that: both halves now open the same destination, so keeping two
   * registries would mean two ways to be wrong about the same link. `rulebookAnchors.ts` is a thin
   * shim over this table and exists only so the Calculator's call sites did not all have to move
   * in the same commit.
   */
  'keeper-cost-and-the-grid': {
    id: 'keeper-cost-and-the-grid',
    label: 'How keeper cost is calculated',
    rules: [{ id: '2017-II-1' }, { id: '2019-II-1' }, { id: '2022-udfa-keeper-value' },
      { id: '2013-III-1' }, { id: '2018-II-1' }],
  },
  'sleeper-reward': {
    id: 'sleeper-reward',
    label: 'Sleeper reward rule',
    rules: [{ id: '2015-III-11' }, { id: '2016-II-1' }, { id: '2017-II-2' },
      { id: '2022-sleeper-reward-amendment' }, { id: '2025-proposal-05-franchise-tag' }],
  },
  'luxury-tax': {
    id: 'luxury-tax',
    label: 'Luxury tax rule',
    rules: [{ id: '2018-III-5' }, { id: '2019-III-3' }, { id: '2020-III-1' },
      { id: '2023-keeper-expansion-eight' }, { id: '2019-I-1' }, { id: '2020-I-2' }],
  },
  'trade-does-not-reset-keeper-standing': {
    id: 'trade-does-not-reset-keeper-standing',
    label: 'Why a trade does not reset keeper standing',
    rules: [{ id: '2013-III-3' }, { id: '2018-III-2' }],
  },
};

/**
 * Live since League › Rules landed (22 August). One constant, exactly as promised: every `§`
 * already wired to an anchor came alive at once and none had to be found again.
 *
 * What "live" means here is deliberately modest. The panel still cannot render a SECTION of a rule
 * or a two-rule reading as one titled answer — the two things the note above wanted. What it can
 * now do is send the reader to the rulebook with the anchor's FIRST rule open and its group
 * selected, where the other rules in the anchor are one row away and visibly related. That answers
 * the question the mark is asking, so the mark stops being a dead end.
 */
export const ANCHORS_LIVE = true;

/** The anchor a `§` should open, or null while the destination does not exist. */
export function anchorTarget(id: string): RuleAnchor | null {
  const anchor = RULE_ANCHORS[id];
  if (!anchor) return null;
  return ANCHORS_LIVE ? anchor : null;
}

/** Declared but not reachable — what a `§` renders as an inert mark rather than a link. */
export const anchorDeclared = (id: string): boolean => id in RULE_ANCHORS;

/**
 * The rule a `§` opens: the anchor's first, which is always the one that CREATED the thing being
 * cited. The later ids in an anchor amend it, and landing on an amendment without having read what
 * it amends is the wrong way round.
 */
export function anchorOpensRule(id: string): string | null {
  const anchor = anchorTarget(id);
  return anchor?.rules[0]?.id ?? null;
}

/** `#/league/rules/2014-II-1` — a link that can be pasted into Discord and still work. */
export function anchorHref(id: string): string | null {
  const rule = anchorOpensRule(id);
  return rule ? `#/league/rules/${rule}` : null;
}
